import type { FileGraphNode, FileGraphResponse, FileLinkRelationType, RelatedFileEntry } from '@/types'

/**
 * Pure helpers that build and merge the data handed to react-force-graph.
 *
 * The library mutates node objects in place (x/y/vx/vy) and rewrites each
 * link's source/target from an id to the node object. So: node references are
 * reused across merges to keep their positions, and `source`/`target` are
 * always read through `endpointId()`.
 */

// Type aliases (not interfaces) so they satisfy the library's index-signature node/link types.
export type GraphNodeDatum = {
  id: number
  name: string
  mime_type: string | null
  folder_id: number | null
  tags: { tag: string; color: string | null }[]
  isCenter: boolean
  /** Its own neighborhood has been fetched and merged. */
  expanded: boolean
  x?: number
  y?: number
  vx?: number
  vy?: number
  fx?: number
  fy?: number
}

export type GraphLinkDatum = {
  key: string
  kind: 'link' | 'related'
  /** Present for explicit links only. */
  linkId?: number
  source: number | GraphNodeDatum
  target: number | GraphNodeDatum
  relation_type?: FileLinkRelationType
  note?: string | null
  /** 0..1 relevance for `related` edges. */
  score?: number
  curvature?: number
}

export interface GraphData {
  nodes: GraphNodeDatum[]
  links: GraphLinkDatum[]
}

export const EMPTY_GRAPH: GraphData = { nodes: [], links: [] }

const RECIPROCAL_CURVATURE = 0.25
const SEED_JITTER = 18

export function endpointId(endpoint: number | GraphNodeDatum): number {
  return typeof endpoint === 'number' ? endpoint : endpoint.id
}

export function pairKey(a: number, b: number): string {
  return a < b ? `${a}:${b}` : `${b}:${a}`
}

function toNode(node: FileGraphNode, centerId: number): GraphNodeDatum {
  const isCenter = node.id === centerId
  return {
    id: node.id,
    name: node.name,
    mime_type: node.mime_type,
    folder_id: node.folder_id,
    tags: node.tags ?? [],
    isCenter,
    expanded: isCenter,
    ...(isCenter ? { fx: 0, fy: 0 } : {}),
  }
}

/** Mark reciprocal explicit links (A→B and B→A) so they render as two arcs instead of overlapping. */
function applyCurvature(links: GraphLinkDatum[]): GraphLinkDatum[] {
  const explicitByPair = new Map<string, number>()
  for (const link of links) {
    if (link.kind !== 'link') continue
    const key = pairKey(endpointId(link.source), endpointId(link.target))
    explicitByPair.set(key, (explicitByPair.get(key) ?? 0) + 1)
  }
  for (const link of links) {
    if (link.kind !== 'link') continue
    const key = pairKey(endpointId(link.source), endpointId(link.target))
    link.curvature = (explicitByPair.get(key) ?? 0) > 1 ? RECIPROCAL_CURVATURE : 0
  }
  return links
}

function edgesToLinks(res: FileGraphResponse): GraphLinkDatum[] {
  return res.edges
    .filter((e) => e.source !== e.target)
    .map((e) => ({
      key: `link:${e.id}`,
      kind: 'link' as const,
      linkId: e.id,
      source: e.source,
      target: e.target,
      relation_type: e.relation_type,
      note: e.note,
    }))
}

export function buildInitialGraph(centerId: number, res: FileGraphResponse): GraphData {
  const nodes = res.nodes.map((n) => toNode(n, centerId))
  const nodeIds = new Set(nodes.map((n) => n.id))
  const links = edgesToLinks(res).filter((l) => nodeIds.has(endpointId(l.source)) && nodeIds.has(endpointId(l.target)))
  return { nodes, links: applyCurvature(links) }
}

/**
 * Merge a neighborhood fetched for `expandedId` into the existing graph.
 * Existing node objects are reused; new nodes are seeded next to the expanded
 * node so they grow out of it instead of flying in from the origin.
 */
export function mergeGraphResponse(
  prev: GraphData,
  res: FileGraphResponse,
  expandedId: number,
  maxNodes: number
): { data: GraphData; hitCap: boolean } {
  const centerId = prev.nodes.find((n) => n.isCenter)?.id ?? expandedId
  const byId = new Map(prev.nodes.map((n) => [n.id, n]))
  const anchor = byId.get(expandedId)
  const nodes = [...prev.nodes]
  let hitCap = false

  for (const incoming of res.nodes) {
    const existing = byId.get(incoming.id)
    if (existing) continue
    if (nodes.length >= maxNodes) {
      hitCap = true
      continue
    }
    const node = toNode(incoming, centerId)
    if (anchor && anchor.x !== undefined && anchor.y !== undefined) {
      node.x = anchor.x + (Math.random() - 0.5) * SEED_JITTER * 2
      node.y = anchor.y + (Math.random() - 0.5) * SEED_JITTER * 2
    }
    byId.set(node.id, node)
    nodes.push(node)
  }

  if (anchor) anchor.expanded = true

  const seenLinkIds = new Set(prev.links.filter((l) => l.linkId !== undefined).map((l) => l.linkId as number))
  const links = prev.links.map((l) => ({ ...l }))
  for (const link of edgesToLinks(res)) {
    if (link.linkId !== undefined && seenLinkIds.has(link.linkId)) continue
    if (!byId.has(endpointId(link.source)) || !byId.has(endpointId(link.target))) continue
    seenLinkIds.add(link.linkId as number)
    links.push(link)
  }

  // A newly-learned explicit link between two nodes supersedes any suggestion between them.
  const explicitPairs = new Set(
    links.filter((l) => l.kind === 'link').map((l) => pairKey(endpointId(l.source), endpointId(l.target)))
  )
  const deduped = links.filter(
    (l) => l.kind !== 'related' || !explicitPairs.has(pairKey(endpointId(l.source), endpointId(l.target)))
  )

  return { data: { nodes, links: applyCurvature(deduped) }, hitCap }
}

/**
 * Overlay semantic "related" suggestions as dashed edges from the center.
 * Suggestions that duplicate an explicit link are dropped; suggested files
 * not yet in the graph are added as nodes (subject to `maxNodes`).
 */
export function applyRelatedOverlay(
  data: GraphData,
  centerId: number,
  related: RelatedFileEntry[],
  maxNodes: number
): GraphData {
  if (related.length === 0) return data

  const byId = new Map(data.nodes.map((n) => [n.id, n]))
  const explicitPairs = new Set(
    data.links.filter((l) => l.kind === 'link').map((l) => pairKey(endpointId(l.source), endpointId(l.target)))
  )
  const nodes = [...data.nodes]
  const links = data.links.filter((l) => l.kind !== 'related')

  for (const entry of related) {
    const peerId = entry.file.id
    if (peerId === centerId) continue
    if (explicitPairs.has(pairKey(centerId, peerId))) continue

    if (!byId.has(peerId)) {
      if (nodes.length >= maxNodes) continue
      const node: GraphNodeDatum = {
        id: peerId,
        name: entry.file.display_name || entry.file.original_name || entry.file.name,
        mime_type: entry.file.mime_type ?? null,
        folder_id: entry.file.folder_id ?? null,
        tags: (entry.file.tags ?? []).map((t) => ({ tag: t.tag, color: t.color ?? null })),
        isCenter: false,
        expanded: false,
      }
      byId.set(peerId, node)
      nodes.push(node)
    }

    links.push({
      key: `related:${centerId}:${peerId}`,
      kind: 'related',
      source: centerId,
      target: peerId,
      score: Math.max(0, Math.min(1, entry.score)),
    })
  }

  return { nodes, links }
}

/** The explicit link (if any) joining two nodes, in either direction. */
export function explicitLinkBetween(data: GraphData, a: number, b: number): GraphLinkDatum | undefined {
  const key = pairKey(a, b)
  return data.links.find((l) => l.kind === 'link' && pairKey(endpointId(l.source), endpointId(l.target)) === key)
}

/** The suggestion edge (if any) joining two nodes. */
export function relatedLinkBetween(data: GraphData, a: number, b: number): GraphLinkDatum | undefined {
  const key = pairKey(a, b)
  return data.links.find((l) => l.kind === 'related' && pairKey(endpointId(l.source), endpointId(l.target)) === key)
}

/** Whether a node is connected to the center by any edge (used to drop orphans after unlinking). */
export function hasAnyEdge(data: GraphData, nodeId: number): boolean {
  return data.links.some((l) => endpointId(l.source) === nodeId || endpointId(l.target) === nodeId)
}
